import React, { useEffect, useRef, useState } from "react";
import { Range } from "react-range";
// ⚠️ This import is byte-identical in both apps by luck AND by checking:
// `getProgressBarColour` is the same function in each repo's `generalUtils.js`,
// and `AnnualOkrScorecard/` and `AnnualOkrViewOkrs/` sit at the same depth. If
// either ever moves, this file stops being copyable and the two boards drift.
import { getProgressBarColour } from "../../../../utils/generalUtils";
// Same chip as the interview board and the review screens (byte-identical in
// both apps, at the same relative path from both scorecard folders).
import WigLinkChip from "../_shared/WigLinkChip";
// Same relative path from both scorecard folders, and ElegantTooltip itself is
// byte-identical in both repos, so this import keeps the file copyable.
import { ElegantTooltip } from "../../../shared/formComponents/ElegantTooltip/ElegantTooltip";
// AO-443: the "FY27 (Jul 2026 – Jun 2027)" line. Byte-identical in both apps,
// at the same relative path from both scorecard folders.
import { formatPlanPeriod } from "../_shared/planPeriod";
import "./_okr-scorecard.scss";

// ═════════════════════════════════════════════════════════════════════════════
// Annual OKR Scorecard — the locked board, post-§7.5.
//
// ⚠️ THIS FILE IS IDENTICAL IN BOTH APPS (CEO `AnnualOkrScorecard/` and coach
// `AnnualOkrViewOkrs/`), and so is `_okr-scorecard.scss`. `diff` is the test.
// The only differences between the two screens are the props the CEO app
// passes and the coach app doesn't: `canEditObjective`, `explainLocked` and
// `footnote`. The coach's View OKRs is always read-only.
//
// ── Redesign, 2026-10-09 (stakeholder feedback on six options) ──────────────
// Option E's accordion, refined:
//   • One CARD per Objective, stacked in the CEO's priority order. Never
//     grouped or re-sorted by WIG or status — the order IS the strategy.
//   • No table column header. The collapsed card shows the four things a CEO
//     scans for: the Objective, its owner (+ linked WIG), its progress and its
//     status. Key Results only appear when a card is opened.
//   • ONE progress per level. Objective progress = the average of its Key
//     Results' `progress_percent` (the API has no Objective-level number). The
//     scorecard figure in `ScorecardHeader` = the average of the Objectives.
//   • ⚠️ EVERY KEY RESULT SHOWS ITS OWN BAR, no exceptions (2026-10-09). An
//     Objective with one Key Result used to hide that row's bar and put the
//     slider on the Objective row instead, so some Key Results had no visible
//     progress at all. Now the Objective row is ALWAYS a read-only summary and
//     every Key Result is edited on its own line.
//   • One "Key Results" heading over the list, not "KR 1 / KR 2" per line. The
//     Quarterly OKR scorecard can add an "Initiatives" section in the same body
//     and contributors in the same meta line without changing this layout.
//   • Objective % is larger than a Key Result % so the two levels read apart.
//   • Meta line under the statement, in this order (2026-10-09): owner, the
//     WIG link icon (`WigLinkChip` — WIG name in its tooltip, as on every
//     other Annual OKR screen), then the dimension pill ("Financial"…). WIG
//     and dimension are metadata, never a grouping.
//
// ⚠️ THINGS THE CONTRACT STILL DOES NOT SUPPORT (unchanged from before):
//   1. Contributors — §4.6 models one `owner_name`.
//   2. "At Risk" / "Completed" / "Not Started" — §4.5 validates
//      `progress_status` against ON_TRACK | IN_PROGRESS | OFF_TRACK and 400s on
//      anything else. They stay selectable on instruction; the store rolls back.
// ═════════════════════════════════════════════════════════════════════════════

// ── The five statuses ───────────────────────────────────────────────────────
//   Not Started  → NOT_STARTED  ❌ 400 (also the `null` a row arrives with)
//   At Risk      → AT_RISK      ❌ 400
//   Off Track    → OFF_TRACK    ✅ saves
//   On Track     → ON_TRACK     ✅ saves
//   Completed    → COMPLETED    ❌ 400
// `tone` is a CSS modifier suffix; `_okr-scorecard.scss` holds the colours.
export const PROGRESS_OPTIONS = [
  { value: "NOT_STARTED", label: "Not Started", tone: "idle" },
  { value: "AT_RISK", label: "At Risk", tone: "risk" },
  { value: "OFF_TRACK", label: "Off Track", tone: "off" },
  { value: "ON_TRACK", label: "On Track", tone: "on" },
  { value: "COMPLETED", label: "Completed", tone: "done" },
];

// The three §4.5 accepts. Exported so a caller can tell an "unsupported yet"
// failure from a real one without re-deriving the list.
export const SERVER_SUPPORTED_PROGRESS = [
  "ON_TRACK",
  "IN_PROGRESS",
  "OFF_TRACK",
];

// A status the server may still hold but the dropdown no longer offers. Without
// this an IN_PROGRESS row would fall through to "Not Started" and misreport.
const LEGACY_PROGRESS_OPTIONS = [
  { value: "IN_PROGRESS", label: "In Progress", tone: "idle" },
];

const optionFor = (status) =>
  PROGRESS_OPTIONS.find((option) => option.value === status) ||
  LEGACY_PROGRESS_OPTIONS.find((option) => option.value === status) ||
  PROGRESS_OPTIONS[0];

const clampPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.min(100, Math.max(0, Math.round(number)));
};

const krPercent = (kr) => clampPercent(kr?.progress_percent);

// Objective progress: the average of its Key Results. Exported for the header.
export const objectivePercent = (objective) => {
  const krs = objective?.key_results || [];
  if (!krs.length) return 0;
  return Math.round(
    krs.reduce((sum, kr) => sum + krPercent(kr), 0) / krs.length,
  );
};

// The text colour for a percentage, matched to `getProgressBarColour`'s three
// bands (≤30 / ≤70 / >70). The bar colours themselves are too light to read as
// text, so these are the scorecard's own status inks — AA on white.
const percentInk = (value) =>
  value <= 30 ? "#c22f2f" : value <= 70 ? "#9c5115" : "#10794a";

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");

// ── Dimension pill ──────────────────────────────────────────────────────────
// `objective.dimension` = { key: "financial_wig", label: "Financial", position,
// total }. Shows `label`; a missing dimension renders nothing.
//
// ⚠️ ONE COLOUR FOR EVERY DIMENSION (2026-10-09): the brand teal, by
// instruction. Per-dimension tones (the Blueprint's palette) were tried and
// dropped — four colours on one meta line competed with the status pill.
const DimensionPill = ({ dimension }) =>
  dimension?.label ? <span className="okrs-dim">{dimension.label}</span> : null;

// ── Icons ───────────────────────────────────────────────────────────────────
const ChevronIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

// ── Header: title + plan period on the left, overall progress on the right ──
// AO-443 / AO-449: the period is "FY27 (Jul 2026 – Jun 2027)", built from the
// tracking payload's `start_date` / `end_date` (added by BE 2026-10-16, on both
// the CEO and the coach tracking routes).
//
// ⚠️ NO `cycle_label` FALLBACK ANY MORE. It used to stand in when the dates
// were missing, which is how APP Program companies saw "APP" under the title —
// the programme's name, not a period. BE keeps `cycle_label` as it is and says
// the dates can still be null for a run with none stored; in that case the
// line is dropped rather than showing the label.
const durationOf = (tracking) =>
  formatPlanPeriod(tracking?.start_date, tracking?.end_date);

// AO-445: the explanation that replaced the "Overall progress" label. Kept in
// step with the `overall` maths in `ScorecardHeader` and `objectivePercent` —
// if either changes, this copy must too.
const OVERALL_HOW =
  "Each objective's progress is the average of its key results. This number is the average of all objectives, so every objective counts equally.";

const OverallTip = ({ count }) => (
  <span className="okrs-overall-tip">
    <span className="okrs-overall-tip__eyebrow">Overall progress</span>
    <span className="okrs-overall-tip__title">
      {`How far the company has got on this year's Company OKRs, across ${
        count === 1 ? "its 1 objective" : `all ${count} objectives`
      }.`}
    </span>
    <span className="okrs-overall-tip__body">{OVERALL_HOW}</span>
  </span>
);

export const ScorecardHeader = ({
  tracking,
  title = "Annual OKR Scorecard",
}) => {
  const objectives = tracking?.objectives || [];
  const duration = durationOf(tracking);
  const overall = objectives.length
    ? Math.round(
        objectives.reduce(
          (sum, objective) => sum + objectivePercent(objective),
          0,
        ) / objectives.length,
      )
    : null;

  return (
    <div className="okrs-header">
      <div className="okrs-header-text">
        <h1 className="okrs-title">{title}</h1>
        {duration ? (
          <span className="okrs-duration">
            <CalendarIcon />
            {duration}
          </span>
        ) : null}
      </div>

      {/* AO-445: the "Overall progress · N objectives" label beside the ring
          was dropped — it didn't fit the header. The ring stays; an info icon
          takes the label's place, and hovering (or focusing) either one opens
          a tooltip saying what the number is and how it's worked out. The
          whole group is one trigger so the icon isn't a tiny hover target. */}
      {overall !== null ? (
        <ElegantTooltip
          content={<OverallTip count={objectives.length} />}
          placement="bottom"
          maxWidth={300}
        >
          <div
            className="okrs-overall"
            // Focusable so keyboard users can open the tooltip too.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
          >
            <i
              className="fa-solid fa-circle-info okrs-overall-info"
              aria-hidden="true"
            />
            <span
              className="okrs-ring"
              style={{
                background: `conic-gradient(${getProgressBarColour(overall)} ${overall}%, #eef0f4 0)`,
              }}
              role="img"
              aria-label={`Overall progress ${overall}%`}
            >
              <span
                className="okrs-ring-inner"
                style={{ color: percentInk(overall) }}
              >
                {overall}%
              </span>
            </span>
            {/* The tooltip only exists on hover/focus; this gives screen
                readers the same explanation without needing it open. */}
            <span className="visually-hidden">{OVERALL_HOW}</span>
          </div>
        </ElegantTooltip>
      ) : null}
    </div>
  );
};

// ── The status control ──────────────────────────────────────────────────────
// ⚠️ NOT A NATIVE `<select>`: the open menu of a native select is drawn by the
// OS, so the coloured dots — the whole point of the five statuses — would be
// invisible until after choosing. A button + absolutely-positioned list, with
// the three behaviours a native select gives for free re-implemented: close on
// outside click, close on Escape, focus back to the trigger.
const StatusDropdown = ({ status, saving, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const option = optionFor(status);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const choose = (value) => {
    setOpen(false);
    if (value !== status) onChange(value);
  };

  return (
    <div className="okrs-status-wrap" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`okrs-status okrs-status--${option.tone} is-editable${open ? " is-open" : ""}`}
        disabled={saving}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className="okrs-dot" aria-hidden="true" />
        <span className="okrs-status-label">{option.label}</span>
        <span className={`okrs-status-caret${open ? " is-flipped" : ""}`}>
          <ChevronIcon size={11} />
        </span>
      </button>

      {open ? (
        <div className="okrs-status-menu" role="listbox">
          {PROGRESS_OPTIONS.map((item) => (
            <button
              type="button"
              key={item.value}
              role="option"
              aria-selected={item.value === status}
              className={`okrs-status-option okrs-status-option--${item.tone}${
                item.value === status ? " is-selected" : ""
              }`}
              onClick={() => choose(item.value)}
            >
              <span className="okrs-dot" aria-hidden="true" />
              <span className="okrs-status-option-label">{item.label}</span>
              {item.value === status ? (
                <i className="fa-solid fa-check" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// The read-only status. With `lockedReason` (AO-448), hovering or focusing it
// says who CAN update it, so a team member isn't left wondering why the pill
// that the CEO and owners can change does nothing for them.
const StatusChip = ({ status, lockedReason }) => {
  const option = optionFor(status);
  const chip = (
    <span
      className={`okrs-status okrs-status--${option.tone}${
        lockedReason ? " is-locked" : ""
      }`}
      // Focusable only when there is a reason to read, so keyboard users can
      // open the tooltip too.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={lockedReason ? 0 : undefined}
    >
      <span className="okrs-dot" aria-hidden="true" />
      <span className="okrs-status-label">{option.label}</span>
      {lockedReason ? (
        <>
          <i className="fa-solid fa-lock okrs-status-lock" aria-hidden="true" />
          {/* The tooltip only exists on hover/focus; this gives screen readers
              the same reason without needing it open. */}
          <span className="visually-hidden">{lockedReason}</span>
        </>
      ) : null}
    </span>
  );

  if (!lockedReason) return chip;
  return (
    <ElegantTooltip content={lockedReason} placement="top" maxWidth={260}>
      {chip}
    </ElegantTooltip>
  );
};

// AO-448: why this viewer can't update the objective. AO-447's rule — the
// objective's single owner and the CEO can update it, nobody else; an
// objective with no owner is CEO-only (BE, 2026-10-16).
const lockedReasonFor = (objective) =>
  objective?.owner_name
    ? `Only ${objective.owner_name} and the CEO can update this objective's status, because ${objective.owner_name} owns it.`
    : "Only the CEO can update this objective's status, because it has no owner.";

// ── Progress: one bar, read-only or draggable ───────────────────────────────
// `level` sets the size of the percentage: an Objective's is larger than a Key
// Result's so the two levels read apart.
const ProgressBar = ({ percent, level }) => (
  <span className={`okrs-prog okrs-prog--${level}`}>
    <span className="okrs-bar">
      <span
        className="okrs-bar-fill"
        style={{
          width: `${percent}%`,
          background: getProgressBarColour(percent),
        }}
      />
    </span>
    <span className="okrs-pct" style={{ color: percentInk(percent) }}>
      {percent}%
    </span>
  </span>
);

// The CEO's slider. ⚠️ ITS THUMB IS ALWAYS VISIBLE (2026-10-09), small at
// rest and growing on hover/focus/drag, so a draggable bar is recognisable
// before the pointer finds it. Read-only bars (`ProgressBar`) have no thumb at
// all: a thumb promises a drag. Writes on `onFinalChange` only (once per
// drag, not per pixel); the store's PATCH is optimistic with rollback.
const ProgressSlider = ({ kr, level, label, onChange }) => {
  const serverPercent = krPercent(kr);
  const [percent, setPercent] = useState(serverPercent);

  // A rollback or another tab's save changes the prop; follow it.
  useEffect(() => {
    setPercent(serverPercent);
  }, [serverPercent]);

  return (
    <span className={`okrs-prog okrs-prog--${level} is-editable`}>
      <Range
        step={1}
        min={0}
        max={100}
        values={[percent]}
        onChange={(values) => setPercent(clampPercent(values[0]))}
        onFinalChange={(values) => onChange?.(clampPercent(values[0]))}
        renderTrack={({ props, children }) => (
          <span
            {...props}
            className="okrs-bar okrs-bar--track"
            style={props.style}
          >
            <span
              className="okrs-bar-fill"
              style={{
                width: `${percent}%`,
                background: getProgressBarColour(percent),
              }}
            />
            {children}
          </span>
        )}
        renderThumb={({ props }) => (
          <span
            {...props}
            className="okrs-thumb"
            aria-label={label}
            style={props.style}
          />
        )}
      />
      <span className="okrs-pct" style={{ color: percentInk(percent) }}>
        {percent}%
      </span>
    </span>
  );
};

// ── One Objective ───────────────────────────────────────────────────────────
const ObjectiveCard = ({
  objective,
  index,
  canEdit,
  explainLocked,
  saving,
  defaultOpen,
  onChangeStatus,
  onChangeKrValue,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const status = objective.progress_status || "NOT_STARTED";
  const keyResults = objective.key_results || [];
  const percent = objectivePercent(objective);
  const ownerName = objective.owner_name;
  const bodyId = `okrs-body-${objective.annual_objective_id}`;

  const saveKr = (kr) => (value) =>
    onChangeKrValue?.(objective.annual_objective_id, kr.key_result_id, value);

  // The whole head toggles, except the controls inside it — a status pick or
  // the WIG link icon must never also open or close the card.
  const onHeadClick = (event) => {
    if (event.target.closest(".okrs-status-wrap, .aowig")) return;
    setOpen((wasOpen) => !wasOpen);
  };

  return (
    <article className={`okrs-card${open ? " is-open" : ""}`}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="okrs-card-head" onClick={onHeadClick}>
        <span className="okrs-num">{String(index + 1).padStart(2, "0")}</span>

        <div className="okrs-card-main">
          <div className="okrs-statement">
            {objective.objective_statement || "Untitled Objective"}
          </div>
          <div className="okrs-meta">
            <span className="okrs-person">
              <span className="okrs-avatar" aria-hidden="true">
                {initialsOf(ownerName) || <i className="fa-solid fa-user" />}
              </span>
              {ownerName ||
                (objective.owner_user_id != null
                  ? "Owner name not available"
                  : "No owner set")}
            </span>
            {/* `wig_label` passed through as-is: a string shows the link
                icon (name in the tooltip), null shows "Not linked", and a
                payload without the field (§4.6 before AO-426) shows nothing. */}
            <WigLinkChip wigLabel={objective.wig_label} />
            <DimensionPill dimension={objective.dimension} />
          </div>
        </div>

        {/* Always the read-only average — Key Results are edited on their
            own lines below. */}
        <ProgressBar percent={percent} level="obj" />

        {canEdit ? (
          <StatusDropdown
            status={status}
            saving={saving}
            onChange={onChangeStatus}
          />
        ) : (
          <StatusChip
            status={status}
            lockedReason={explainLocked ? lockedReasonFor(objective) : null}
          />
        )}

        <button
          type="button"
          className="okrs-chevron"
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={open ? "Hide key results" : "Show key results"}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((wasOpen) => !wasOpen);
          }}
        >
          <ChevronIcon />
        </button>
      </div>

      {open ? (
        <div className="okrs-card-body" id={bodyId}>
          <div className="okrs-section-title">
            Key Results
            <span className="okrs-section-count">{keyResults.length}</span>
          </div>
          {keyResults.length ? (
            keyResults.map((kr, krIndex) => (
              <div
                className="okrs-line"
                key={kr.key_result_id ?? kr.sequence ?? krIndex}
              >
                <span className="okrs-line-text">
                  <span className="okrs-bullet" aria-hidden="true" />
                  <span>{kr.statement || "—"}</span>
                </span>
                {/* Every Key Result, always — see the file header. */}
                {canEdit ? (
                  <ProgressSlider
                    kr={kr}
                    level="kr"
                    label={`Progress for key result ${krIndex + 1}`}
                    onChange={saveKr(kr)}
                  />
                ) : (
                  <ProgressBar percent={krPercent(kr)} level="kr" />
                )}
                <span />
                <span />
              </div>
            ))
          ) : (
            <p className="okrs-line-empty">No key results on this objective.</p>
          )}
        </div>
      ) : null}
    </article>
  );
};

// ── The board ───────────────────────────────────────────────────────────────
// ⚠️ EDITING IS DECIDED PER OBJECTIVE (AO-447). It used to be one `canEdit`
// for the whole board, because only the CEO could update anything. Now the
// objective's owner can too, so the caller passes `canEditObjective(objective)`
// and each card asks it. No function = read-only, which is the coach app.
//
//   explainLocked  show AO-448's "only <owner> and the CEO can update…"
//                  tooltip on a card the viewer can't edit. The CEO app turns
//                  it on for team members; the coach app leaves it off — a
//                  coach never edits here, so the hint would only be noise.
//   footnote       the line under the list, or nothing. The caller words it,
//                  because what's true ("the order you set", "the objectives
//                  you own") depends on who is looking.
const OkrScorecardBoard = ({
  tracking,
  loading,
  canEditObjective,
  explainLocked = false,
  footnote = null,
  savingId = null,
  onChangeStatus,
  onChangeKrValue,
}) => {
  if (loading) {
    // Card-shaped placeholders at the collapsed card's height, so the list
    // doesn't jump when the real cards land.
    return (
      <div className="okrs-list" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="okrs-card okrs-card--sk" />
        ))}
      </div>
    );
  }

  const objectives = tracking?.objectives || [];

  if (!objectives.length) {
    return (
      <div className="okrs-list">
        <div className="okrs-empty">
          <i className="fa-regular fa-file-lines" aria-hidden="true" />
          <p className="okrs-empty-title">Nothing to track yet</p>
          <p className="okrs-empty-text">
            Objectives appear here once the Company OKRs are locked.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="okrs-list">
        {objectives.map((objective, index) => (
          <ObjectiveCard
            key={objective.annual_objective_id}
            objective={objective}
            index={index}
            canEdit={!!canEditObjective?.(objective)}
            explainLocked={explainLocked}
            defaultOpen={index === 0}
            saving={
              savingId != null &&
              String(savingId) === String(objective.annual_objective_id)
            }
            onChangeStatus={(status) =>
              onChangeStatus?.(objective.annual_objective_id, status)
            }
            onChangeKrValue={onChangeKrValue}
          />
        ))}
      </div>
      {/* AO-446 dropped the read-only "Shown in the order the CEO set…" line.
          AO-447 moved the wording out to the caller (see `footnote` above):
          only someone who can edit gets a hint, since dragging a progress bar
          to save isn't discoverable otherwise. */}
      {footnote ? <p className="okrs-footnote">{footnote}</p> : null}
    </>
  );
};

export default OkrScorecardBoard;
